import { useMemo } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import { Badge, Box, Button, Flex, Heading } from '@chakra-ui/react'

import { isTeamVisible } from '@/helpers/team'
import {
  useListLastCustomSummaryPrompts,
  useListTeamCustomSummaryPrompts,
} from '@/react-query/custom-summary-parts'
import { useInterview } from '@/react-query/interview/interview'
import { useTeam } from '@/react-query/team'
import { PlusIcon } from '@/views/assets/PlusIcon'

import { CustomPromptButton } from './CustomPromptButton'

export type PromptTemplate = {
  name: string
  text: string
  hasScoring?: boolean
}

type FilteredTemplate = PromptTemplate & { id: string }

const filterTemplates = (
  templates: FilteredTemplate[],
  existingNames: string[],
  defaultTemplates: PromptTemplate[],
  existingPromptIds: string[],
): FilteredTemplate[] => {
  return templates
    .filter(
      (template, index, self) =>
        index ===
        self.findIndex(
          (t) => t.text === template.text && t.name === template.name,
        ),
    )
    .filter((template) => !existingNames.includes(template.name))
    .filter((template) => !existingPromptIds.includes(template.id))
    .filter(
      (template) =>
        !defaultTemplates.find(
          (t) => t.name === template.name && t.text === template.text,
        ),
    )
}

const customPromptTemplates: PromptTemplate[] = [
  {
    name: 'Motivation',
    text: `Please provide a concise summary of the candidate's motivation for applying to this position, their career aspirations, and what generally drives their professional engagement. Why they do what they do? What they are looking for in a new role

Length and format: please keep it very short, 2-3 simple bullet points long.`,
  },
  {
    name: 'Career goals',
    text: `Please provide a concise summary of the candidate's career goals and aspirations. What they are looking for in a new role? Who do they want to be in the future?

Length and format: please keep it very short, 2 simple bullet points long.`,
  },
  {
    name: 'Tech stack',
    text: `Please provide a concise summary of the candidate's tech stack. 

Format the output like bullet points, each bullet mentioning one specific technology or programming language. 
For each technology, please add very brief details that were mentioned during the interview.`,
  },
  {
    name: 'Latest project',
    text: `Please provide a very short story of the candidate's most recent project as discussed during the interview.
    Make sure to keep all facts and numbers that were mentioned by the candidate. 
    
If discussed, please mention:
  - What the project was about?
  - Where did the idea come from and why?
  - What was the process?
  - What were the outcomes?
  - What were key takeaways and lessons learned?
    
Length and format: one paragraph, 3-5 sentences.`,
  },
  {
    name: 'Specific experience',
    text: `Please summarize the candidate's specific experience in [insert your topic here] as discussed during the interview.
Make sure to keep all facts and numbers that were mentioned by the candidate that are relevant to this specific experience. 
  
Length and format: one paragraph, 3-5 sentences.`,
  },
]

const scoredTemplates: PromptTemplate[] = [
  {
    name: 'Ownership',
    hasScoring: true,
    text: `Rate how the candidate takes end-to-end responsibility for outcomes; fixes what's broken without waiting for permission.

Examples of what to look for: “I owned X end-to-end”; post-launch fixes; prevention of recurrence; proactive process changes; accountability for misses.

Output format: provide a score from 1-5 (just the number), a one-sentence rationale, and 2-3 short bullets with concrete evidence from the interview (facts, quotes, or outcomes).`,
  },
  {
    name: 'Leadership',
    hasScoring: true,
    text: `Rate how the candidate sets direction, raises the bar, and grows people; creates clarity and momentum across teams.

Examples of what to look for: Mentored others; articulated a vision; defined success; aligned stakeholders; course-corrected a team.

Output format: provide a score from 1-5 (just the number), a one-sentence rationale, and 2-3 short bullets with concrete evidence from the interview (facts, quotes, or outcomes).`,
  },
  {
    name: 'Communication',
    hasScoring: true,
    text: `Rate how the candidate conveys ideas crisply, structured, and audience-aware; listens actively and closes loops.

Examples of what to look for: Uses BLUF/SCQA; clarifies assumptions; asks incisive questions; concise writing; summarizes decisions/actions.

Output format: provide a score from 1-5 (just the number), a one-sentence rationale, and 2-3 short bullets with concrete evidence from the interview (facts, quotes, or outcomes).`,
  },
  {
    name: 'Collaboration',
    hasScoring: true,
    text: `Rate how the candidate works generously across functions; resolves conflict and co-creates solutions.

Examples of what to look for: Names cross-functional partners; handles conflict well; credits others; co-planning stories; effective handoffs.

Output format: provide a score from 1-5 (just the number), a one-sentence rationale, and 2-3 short bullets with concrete evidence from the interview (facts, quotes, or outcomes).`,
  },
  {
    name: 'Problem Solving',
    hasScoring: true,
    text: `Rate how the candidate frames the right problem, decomposes it, and tests hypotheses to reach pragmatic solutions fast.

Examples of what to look for: Distinguishes symptom vs cause; breaks into sub-problems; states assumptions; evaluates options; runs small experiments.

Output format: provide a score from 1-5 (just the number), a one-sentence rationale, and 2-3 short bullets with concrete evidence from the interview (facts, quotes, or outcomes).`,
  },
]

export const CustomPromptsLibrary = ({
  setIsAddCustomSummaryPartShown,
  setPromptTemplate,
}: {
  setIsAddCustomSummaryPartShown: (isAddCustomSummaryPartShown: boolean) => void
  setPromptTemplate: (promptTemplate: PromptTemplate | undefined) => void
}) => {
  const { data: interview } = useInterview()

  const handleSelectTemplate = (template: PromptTemplate) => {
    setPromptTemplate(template)
    setIsAddCustomSummaryPartShown(true)
  }

  const summaryParts = (interview?.SummaryParts || []).sort(
    (a, b) => a.order - b.order,
  )

  const customSummaryPartsNames = summaryParts
    .map((part) => part.CustomSummaryPartPrompt?.name)
    .filter((name) => name !== undefined) as string[]

  const roleCustomPromptIds =
    interview?.Role?.CustomSummaryPartPrompt?.map((p) => p.id) || []

  const isIntake = interview?.type === 'INTAKE'
  return (
    <Box
      className={
        'border-[1px] border-[#E0E0E0] rounded-[2.5rem] pt-[1.25rem] pb-[1.75rem] px-[2.5rem]'
      }
      boxSizing="border-box"
    >
      <Heading
        className={'flex items-center'}
        fontWeight="bold"
        fontSize="1.25rem"
        flexGrow={1}
        mt={2}
        as="h2"
        alignSelf={'stretch'}
      >
        Add your own custom topic
      </Heading>
      <YourCustomPrompts
        existingCustomSummaryPartsNames={customSummaryPartsNames}
        roleCustomPromptIds={roleCustomPromptIds}
        onSelectTemplate={handleSelectTemplate}
      />
      <TeamPrompts
        customSummaryPartsNames={customSummaryPartsNames}
        roleCustomPromptIds={roleCustomPromptIds}
        onSelectTemplate={handleSelectTemplate}
      />
      <DefaultPrompts
        customSummaryPartsNames={customSummaryPartsNames}
        isIntake={isIntake}
        onSelectTemplate={handleSelectTemplate}
      />
      <CompetenciesPrompts
        customSummaryPartsNames={customSummaryPartsNames}
        onSelectTemplate={handleSelectTemplate}
      />
      <Button
        variant={'brand-solid'}
        leftIcon={<PlusIcon stroke="white" />}
        onClick={() => {
          setPromptTemplate(undefined)
          setIsAddCustomSummaryPartShown(true)
        }}
        mt={12}
      >
        Create your own
      </Button>
    </Box>
  )
}

const YourCustomPrompts = ({
  existingCustomSummaryPartsNames,
  roleCustomPromptIds,
  onSelectTemplate,
}: {
  existingCustomSummaryPartsNames: string[]
  roleCustomPromptIds: string[]
  onSelectTemplate: (template: PromptTemplate) => void
}) => {
  const lastTemplatesQuery = useListLastCustomSummaryPrompts()

  const lastTemplates = useMemo(
    () =>
      lastTemplatesQuery.data
        ? filterTemplates(
            lastTemplatesQuery.data.pages.flatMap(
              (page) => page.customSummaryPrompts,
            ),
            existingCustomSummaryPartsNames,
            [...customPromptTemplates, ...scoredTemplates],
            roleCustomPromptIds,
          )
        : [],
    [
      lastTemplatesQuery.data,
      existingCustomSummaryPartsNames,
      roleCustomPromptIds,
    ],
  )

  if (!lastTemplatesQuery.data || lastTemplates.length === 0) return null

  return (
    <Box mt={12}>
      <Heading as="h3" fontSize="1rem" fontWeight="bold">
        Added by you previously
      </Heading>
      <Flex gap={2.5} wrap={'wrap'} mb={2.5} mt={6}>
        <AnimatePresence mode="popLayout">
          {lastTemplates.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              layout
            >
              <CustomPromptButton
                template={template}
                onSelectTemplate={onSelectTemplate}
                isDeletable={true}
              />
            </motion.div>
          ))}
        </AnimatePresence>
        {lastTemplatesQuery.hasNextPage && (
          <Button
            isLoading={lastTemplatesQuery.isFetchingNextPage}
            variant={'brand-solid-white'}
            onClick={() => {
              lastTemplatesQuery.fetchNextPage()
            }}
          >
            more...
          </Button>
        )}
      </Flex>
    </Box>
  )
}

const TeamPrompts = ({
  customSummaryPartsNames,
  roleCustomPromptIds,
  onSelectTemplate,
}: {
  customSummaryPartsNames: string[]
  roleCustomPromptIds: string[]
  onSelectTemplate: (template: PromptTemplate) => void
}) => {
  const team = useTeam()
  const teamTemplatesQuery = useListTeamCustomSummaryPrompts()

  const teamTemplates = useMemo(
    () =>
      teamTemplatesQuery.data
        ? filterTemplates(
            teamTemplatesQuery.data.pages.flatMap(
              (page) => page.customSummaryPrompts,
            ),
            customSummaryPartsNames,
            [...customPromptTemplates, ...scoredTemplates],
            roleCustomPromptIds,
          )
        : [],
    [teamTemplatesQuery.data, customSummaryPartsNames, roleCustomPromptIds],
  )

  if (!team.data || !isTeamVisible(team.data)) return null
  if (!teamTemplatesQuery.data || teamTemplates.length === 0) return null

  const teamName = team.data.name

  return (
    <Box mt={12}>
      <Heading as="h3" fontSize="1rem" fontWeight="bold">
        Added by teammates from {teamName}
      </Heading>
      <Flex gap={2.5} wrap={'wrap'} mb={2.5} mt={6}>
        <AnimatePresence mode="popLayout">
          {teamTemplates.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              layout
            >
              <CustomPromptButton
                template={template}
                onSelectTemplate={onSelectTemplate}
                isDeletable={true}
              />
            </motion.div>
          ))}
        </AnimatePresence>
        {teamTemplatesQuery.hasNextPage && (
          <Button
            isLoading={teamTemplatesQuery.isFetchingNextPage}
            variant={'brand-solid-white'}
            flexShrink={0}
            onClick={() => {
              teamTemplatesQuery.fetchNextPage()
            }}
          >
            more...
          </Button>
        )}
      </Flex>
    </Box>
  )
}

const DefaultPrompts = ({
  customSummaryPartsNames,
  isIntake,
  onSelectTemplate,
}: {
  customSummaryPartsNames: string[]
  isIntake: boolean
  onSelectTemplate: (template: PromptTemplate) => void
}) => {
  const visibleTemplates = (isIntake ? [] : customPromptTemplates).filter(
    (template) => !customSummaryPartsNames.includes(template.name),
  )

  if (visibleTemplates.length === 0) return null

  return (
    <Box mt={12}>
      <Heading as="h3" fontSize="1rem" fontWeight="bold">
        Default prompts
      </Heading>
      <Flex gap={2.5} wrap={'wrap'} mb={2.5} mt={6}>
        {visibleTemplates.map((template) => (
          <CustomPromptButton
            key={template.name}
            template={template}
            onSelectTemplate={onSelectTemplate}
            isDeletable={false}
          />
        ))}
      </Flex>
    </Box>
  )
}

const CompetenciesPrompts = ({
  customSummaryPartsNames,
  onSelectTemplate,
}: {
  customSummaryPartsNames: string[]
  onSelectTemplate: (template: PromptTemplate) => void
}) => {
  const visibleTemplates = scoredTemplates.filter(
    (template) => !customSummaryPartsNames.includes(template.name),
  )

  if (visibleTemplates.length === 0) return null

  return (
    <Box mt={12}>
      <Flex alignItems="center" gap={2}>
        <Heading as="h3" fontSize="1rem" fontWeight="bold">
          Competencies scoring
        </Heading>
        <Badge
          color="white"
          fontSize="11px"
          fontWeight="bold"
          px={'7px'}
          py={'2px'}
          borderRadius="4px"
          bgColor="#8B77FB"
        >
          NEW
        </Badge>
      </Flex>
      <Flex gap={2.5} wrap={'wrap'} mb={2.5} mt={6}>
        {visibleTemplates.map((template) => (
          <CustomPromptButton
            key={template.name}
            template={template}
            onSelectTemplate={onSelectTemplate}
            isDeletable={false}
          />
        ))}
      </Flex>
    </Box>
  )
}
